import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaMapMarkerAlt,
  FaPhone,
  FaUser,
  FaFlask,
  FaCreditCard,
  FaTruck,
  FaVial,
  FaMoneyBillWave,
  FaCheck,
} from "react-icons/fa";
import { APP_PATH } from "../../../api/backendPath";
import logo from "../../../assets/images/logo.png";
import styles from "../../../styles/__public_appointment_detail.module.scss";
import { formatCustom as tzCustom, formatDateNumeric as tzDateNumeric } from "../../../utils/datetime";
import { formatPercent, formatWholeAmount } from "../../../utils/amountFormat";

const PublicAppointmentDetail = () => {
  const { t, i18n } = useTranslation();
  const { id: orderId } = useParams(); // Get order ID from URL params

  // Mock data for design preview
  const mockOrderDetails = {
    bookingNumber: "LT-0001",
    patient: {
      full_name: "Ahmed",
      surname: "Al-Rashid",
      mobile_number: "+964 770 123 4567",
    },
    tests: [
      { test_name: "Complete Blood Count (CBC)" },
      { test_name: "Lipid Profile" },
      { test_name: "Liver Function Test" },
    ],
    preferredLocation: "HOME_VISIT", // Change to "ON_SITE" to see Visit Center design
    patientAddress: {
      houseFlatFloorNo: "Building 15, Apt 204",
      street: "Al-Mansour Street",
      areaDistrict: "Al-Mansour District",
      city: "Baghdad",
      governorate: "Baghdad",
      country: "Iraq",
    },
    paymentMethod: "COD", // Change to "ONLINE" to see online payment design
    totalAmount: "75000",
    scheduledDate: "2024-01-15",
    scheduledTime: "10:30 AM",
  };

  // Alternative mock data for Visit Center (uncomment to test)
  // const mockOrderDetails = {
  //   bookingNumber: "LT-0002",
  //   patient: {
  //     full_name: "Fatima",
  //  //     surname: "Hassan",
  //     mobile_number: "+964 771 987 6543"
  //   },
  //   tests: [
  //     { test_name: "X-Ray Chest" },
  //     { test_name: "ECG" }
  //   ],
  //   preferredLocation: "ON_SITE",
  //   paymentMethod: "ONLINE",
  //   totalAmount: "45000",
  //   scheduledDate: "2024-01-16",
  //   scheduledTime: "2:00 PM"
  // };

  // State for order details (fetched from API)
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for technician workflow
  const [workflowStatus, setWorkflowStatus] = useState({
    technicianDispatched: false,
    sampleCollectionStarted: false,
    sampleCollected: false,
    paymentCollected: false,
  });

  // State for estimated arrival time
  const [estimatedArrivalMinutes, setEstimatedArrivalMinutes] = useState(30);

  // Format payment status for display
  const formatPaymentStatus = (paymentMethod, totalAmount) => {
    if (!paymentMethod || !totalAmount) return "Payment Pending";

    if (paymentMethod === "COD") {
      return `COD: ${formatWholeAmount(totalAmount)} IQD`;
    } else {
      return `Online Payment: ${formatWholeAmount(totalAmount)} IQD`;
    }
  };

  // Generate Google Maps link for turn-by-turn navigation with current location
  const generateGoogleMapsLink = (address) => {
    if (!address) return "#";

    // Format address in a way that Google Maps can better understand
    const addressParts = [];

    // Add street address first (most important for Google Maps)
    if (address.street) {
      addressParts.push(address.street);
    }

    // Add area/district
    if (address.areaDistrict) {
      addressParts.push(address.areaDistrict);
    }

    // Add city
    if (address.city) {
      addressParts.push(address.city);
    }

    // Add governorate/state
    if (address.governorate) {
      addressParts.push(address.governorate);
    }

    // Add country
    if (address.country) {
      addressParts.push(address.country);
    }

    // Add building/apartment info as additional context (but not first)
    if (address.houseFlatFloorNo) {
      addressParts.push(address.houseFlatFloorNo);
    }

    const addressString = addressParts.join(", ");

    // Use Google Maps navigation mode for turn-by-turn directions with voice guidance
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addressString)}&travelmode=driving&dir_action=navigate`;
  };

  // Get current location and start live navigation (like Zomato)
  const startLiveNavigation = (address) => {
    if (!address) return;

    // Check if geolocation is supported
    if (!navigator.geolocation) {
      alert("Location access is required for live navigation");
      return;
    }

    // Get current position
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        // Format destination address
        const addressParts = [];
        if (address.street) addressParts.push(address.street);
        if (address.areaDistrict) addressParts.push(address.areaDistrict);
        if (address.city) addressParts.push(address.city);
        if (address.governorate) addressParts.push(address.governorate);
        if (address.country) addressParts.push(address.country);

        const destinationString = addressParts.join(", ");

        // Check if on mobile device
        const isMobile =
          /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent,
          );

        if (isMobile) {
          // For mobile: Try to open Google Maps app in navigation mode
          const googleMapsAppUrl = `google.navigation:q=${encodeURIComponent(destinationString)}&mode=d`;
          window.location.href = googleMapsAppUrl;
        } else {
          // For desktop: Open Google Maps with navigation intent
          const navigationUrl = `https://www.google.com/maps/dir/${latitude},${longitude}/${encodeURIComponent(destinationString)}/@${latitude},${longitude},15z/data=!4m2!4m1!3e0!5i2`;
          window.open(navigationUrl, "_blank", "noopener,noreferrer");
        }
      },
      (error) => {
        console.error("Error getting location:", error);
        alert("Please allow location access for live navigation");
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 60000,
      },
    );
  };

  // Handle workflow status updates
  const handleWorkflowUpdate = async (action) => {
    try {
      console.log(
        `[PUBLIC_APPOINTMENT] Updating workflow with action: ${action}`,
      );

      // Prepare request body
      const requestBody = { action };

      // Add estimated arrival time for dispatch action
      if (action === "dispatch") {
        requestBody.estimatedArrivalMinutes = estimatedArrivalMinutes;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${APP_PATH.publicAppointmentWorkflow}/${orderId}/workflow`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        },
      );

      const data = await response.json();
      console.log(`[PUBLIC_APPOINTMENT] Workflow update response:`, data);

      if (data.status) {
        // Refresh the appointment data to get updated status from backend
        await fetchOrderDetails();

        // Show success message
        if (action === "dispatch") {
          alert(
            `Technician dispatched successfully! Patient will be notified with estimated arrival time of ${estimatedArrivalMinutes} minutes.`,
          );
        } else if (action === "start_sample_collection") {
          alert(
            "Sample collection started. Job status updated to In Progress.",
          );
        } else if (action === "complete_job_cod") {
          alert("Cash collected and job marked as completed successfully!");
        } else if (action === "complete_job_online") {
          alert("Job marked as completed successfully!");
        } else {
          alert(`Workflow updated successfully: ${data.message}`);
        }
      } else {
        alert(`Failed to update workflow: ${data.message || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Error updating workflow:", error);
      alert(
        "Failed to update workflow status. Please check your connection and try again.",
      );
    }
  };

  // Format patient name
  const formatPatientName = (patient) => {
    if (!patient) return "N/A";

    const nameParts = [
      patient.full_name,
      patient.surname,
    ].filter(Boolean);

    return nameParts.length > 0 ? nameParts.join(" ") : "N/A";
  };

  // Format test list
  const formatTestList = (tests) => {
    if (!tests || tests.length === 0) return "No tests specified";

    return tests.map((test) => test.test_name || test.testName).join(", ");
  };

  // Format location
  const formatLocation = (orderDetails) => {
    if (!orderDetails) return "N/A";

    if (
      orderDetails.preferredLocation === "HOME_VISIT" ||
      orderDetails.preferredLocation === "Home Visit"
    ) {
      return "Home Visit";
    } else {
      return "Visit Center";
    }
  };

  // Fetch order details on component mount
  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  // Fetch order details from API
  const fetchOrderDetails = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${APP_PATH.publicAppointmentDetails}/${orderId}`,
      );
      const data = await response.json();

      if (data.status && data.data) {
        setOrderDetails(data.data);

        // Set workflow status from API data
        if (data.data.workflowStatus) {
          setWorkflowStatus(data.data.workflowStatus);
        }
      } else if (
        !data.status &&
        data.error_code === "ACCESS_DENIED_VISIT_CENTER"
      ) {
        setError({
          type: "access_denied",
          message:
            data.message ||
            "This page is only accessible for home visit orders.",
          bookingNumber: data.data?.bookingNumber,
        });
      } else {
        setError({ type: "not_found", message: "Appointment not found" });
      }
    } catch (error) {
      console.error("Error fetching appointment details:", error);
      setError({
        type: "error",
        message: "Failed to load appointment details",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={styles["public-container"]}>
        <div className={styles["loading-container"]}>
          <div className={styles["loading-spinner"]}></div>
          <p>Loading appointment details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles["public-container"]}>
        <div className={styles["error-container"]}>
          {error?.type === "access_denied" ? (
            <>
              <div style={{ fontSize: "48px", marginBottom: "20px" }}>🚫</div>
              <h2>Access Restricted</h2>
              <p
                style={{
                  fontSize: "16px",
                  marginBottom: "10px",
                  fontWeight: "500",
                }}
              >
                {error.message}
              </p>
              {error.bookingNumber && (
                <p
                  style={{ fontSize: "14px", color: "#666", marginTop: "15px" }}
                >
                  Booking Number: <strong>{error.bookingNumber}</strong>
                </p>
              )}
              <div
                style={{
                  marginTop: "25px",
                  padding: "15px",
                  backgroundColor: "#f0f9ff",
                  borderRadius: "8px",
                  border: "1px solid #0ea5e9",
                }}
              >
                <p style={{ fontSize: "14px", color: "#0369a1", margin: 0 }}>
                  📍 This is an <strong>on-site/visit center</strong>{" "}
                  appointment. Please visit the lab center at your scheduled
                  time.
                </p>
              </div>
            </>
          ) : (
            <>
              <h2>{error?.type === "not_found" ? "Not Found" : "Error"}</h2>
              <p>{error?.message || "Appointment details not found."}</p>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!orderDetails) {
    return (
      <div className={styles["public-container"]}>
        <div className={styles["error-container"]}>
          <h2>Not Found</h2>
          <p>Appointment details not found.</p>
        </div>
      </div>
    );
  }

  // A booking refunded by the admin is moved to REJECTED (or CANCELLED). In that
  // state the technician must not take any further action. We detect the refund
  // case from the rejection reason to show a clear message.
  const orderStatusUpper = String(orderDetails.status || "").toUpperCase();
  const isRejectedOrCancelled =
    orderStatusUpper === "REJECTED" || orderStatusUpper === "CANCELLED";
  const rejectionReason = orderDetails.rejection_reason || "";
  const isRefundRejection =
    isRejectedOrCancelled && /refund/i.test(rejectionReason);

  return (
    <div className={styles["public-container"]}>
      <div className={styles["logo-header"]}>
        <img src={logo} alt="Tiryaq" />
      </div>

      <div className={styles["public-header"]}>
        <h1>Lab Appointment Details</h1>
        <p>Booking ID: {orderDetails.bookingNumber}</p>
      </div>

      {isRejectedOrCancelled && (
        <div
          style={{
            margin: "0 auto 20px",
            maxWidth: "900px",
            padding: "16px 20px",
            backgroundColor: "#fef2f2",
            border: "1px solid #ef4444",
            borderRadius: "8px",
            color: "#b91c1c",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "18px", fontWeight: 700, marginBottom: "6px" }}>
            {isRefundRejection
              ? "Booking Refunded & Rejected"
              : "Booking Rejected"}
          </div>
          <p style={{ margin: 0, fontSize: "14px" }}>
            {isRefundRejection
              ? "This booking was rejected because the patient's payment has been refunded by the admin. No further action can be taken on this order."
              : "This booking has been rejected and can no longer be processed. No further action can be taken."}
          </p>
        </div>
      )}

      <div className={styles["public-content"]}>
        <div className={styles["detail-card"]}>
          <div className={styles["card-header"]}>
            <FaUser className={styles["card-icon"]} />
            <h2>Patient Information</h2>
          </div>
          <div className={styles["card-content"]}>
            {(() => {
              // Child flow: the clinical patient is the child, so the Name
              // row surfaces the child's name (from the booking snapshot)
              // with a small teal "Child" pill and a separate "Guardian
              // Name" row right below for the account owner's name. Self
              // flow: just the guardian/patient name as before, no badge,
              // no Guardian row.
              const isChildFlow =
                orderDetails?.is_for_child === true ||
                orderDetails?.isForChild === true;
              const childName =
                [
                  orderDetails?.child_name ?? orderDetails?.childName,
                  orderDetails?.child_surname,
                ]
                  .filter(Boolean)
                  .join(" ") || "";
              const guardianName = formatPatientName(orderDetails.patient);
              if (isChildFlow && childName) {
                return (
                  <>
                    <div className={styles["info-row"]}>
                      <span className={styles["info-label"]}>Name:</span>
                      <span
                        className={styles["info-value"]}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "8px",
                          flexWrap: "wrap",
                        }}
                      >
                        <span>{childName}</span>
                        <span
                          // Same corner-badge look used on the officer app
                          // appointment detail — keeps the "Child" indicator
                          // visually consistent across surfaces.
                          style={{
                            fontSize: "11px",
                            background: "#0cbdbd",
                            color: "#fff",
                            padding: "2px 8px",
                            borderRadius: "10px",
                            fontWeight: 500,
                          }}
                        >
                          Child
                        </span>
                      </span>
                    </div>
                    <div className={styles["info-row"]}>
                      <span className={styles["info-label"]}>
                        Guardian Name:
                      </span>
                      <span className={styles["info-value"]}>
                        {guardianName}
                      </span>
                    </div>
                  </>
                );
              }
              return (
                <div className={styles["info-row"]}>
                  <span className={styles["info-label"]}>Name:</span>
                  <span className={styles["info-value"]}>{guardianName}</span>
                </div>
              );
            })()}
            <div className={styles["info-row"]}>
              <span className={styles["info-label"]}>Contact:</span>
              <span className={styles["info-value"]}>
                <FaPhone className={styles["inline-icon"]} />
                {orderDetails.patient?.mobile_number || "N/A"}
              </span>
            </div>
          </div>
        </div>

        <div className={styles["detail-card"]}>
          <div className={styles["card-header"]}>
            <FaFlask className={styles["card-icon"]} />
            <h2>Test Information</h2>
          </div>
          <div className={styles["card-content"]}>
            <div className={styles["info-row"]}>
              <span className={styles["info-label"]}>Tests:</span>
              <span className={styles["info-value"]}>
                {formatTestList(orderDetails.tests)}
              </span>
            </div>
            <div className={styles["info-row"]}>
              <span className={styles["info-label"]}>Service Type:</span>
              <span className={styles["info-value"]}>
                {formatLocation(orderDetails)}
              </span>
            </div>
          </div>
        </div>

        <div className={styles["detail-card"]}>
          <div className={styles["card-header"]}>
            <FaMapMarkerAlt className={styles["card-icon"]} />
            <h2>Location</h2>
          </div>
          <div className={styles["card-content"]}>
            {orderDetails.preferredLocation === "HOME_VISIT" ||
            orderDetails.preferredLocation === "Home Visit" ? (
              <>
                {orderDetails.patientAddress ? (
                  <>
                    <div className={styles["info-row"]}>
                      <span className={styles["info-label"]}>Address:</span>
                      <span className={styles["info-value"]}>
                        {[
                          orderDetails.patientAddress.houseFlatFloorNo,
                          orderDetails.patientAddress.street,
                          orderDetails.patientAddress.areaDistrict,
                          orderDetails.patientAddress.city,
                          orderDetails.patientAddress.governorate,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    </div>
                    <div className={styles["info-row"]}>
                      <span className={styles["info-label"]}>Navigation:</span>
                      <span className={styles["info-value"]}>
                        <button
                          onClick={() =>
                            startLiveNavigation(orderDetails.patientAddress)
                          }
                          className={styles["maps-link"]}
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                          }}
                        >
                          <FaMapMarkerAlt className={styles["inline-icon"]} />
                          Live Navigation
                        </button>
                      </span>
                    </div>
                  </>
                ) : (
                  <div className={styles["info-row"]}>
                    <span className={styles["info-value"]}>
                      Home Visit - Address details not available
                    </span>
                  </div>
                )}
              </>
            ) : (
              <div className={styles["info-row"]}>
                <span className={styles["info-value"]}>
                  Visit Center - Please visit our lab facility
                </span>
              </div>
            )}
          </div>
        </div>

        <div className={styles["detail-card"]}>
          <div className={styles["card-header"]}>
            <FaCreditCard className={styles["card-icon"]} />
            <h2>Payment Information</h2>
          </div>
          <div className={styles["card-content"]}>
            <div className={styles["info-row"]}>
              <span className={styles["info-label"]}>Status:</span>
              <span className={styles["info-value"]}>
                {formatPaymentStatus(
                  orderDetails.paymentMethod,
                  orderDetails.totalAmount,
                )}
              </span>
            </div>
            {orderDetails.pricing && (
              <>
                <div className={styles["info-row"]}>
                  <span className={styles["info-label"]}>Service Price:</span>
                  <span className={styles["info-value"]}>
                    {formatWholeAmount(orderDetails.pricing.subtotal || 0)}{" "}
                    IQD
                  </span>
                </div>
                {orderDetails.pricing.transportationFee > 0 && (
                  <div className={styles["info-row"]}>
                    <span className={styles["info-label"]}>Transportation Fee:</span>
                    <span className={styles["info-value"]}>
                      {formatWholeAmount(orderDetails.pricing.transportationFee)}{" "}
                      IQD
                    </span>
                  </div>
                )}
                {orderDetails.pricing.adminFee > 0 && (
                  <div className={styles["info-row"]}>
                    <span className={styles["info-label"]}>
                      Admin Fee
                      {orderDetails.pricing.adminFeePercentage
                        ? ` (${formatPercent(orderDetails.pricing.adminFeePercentage)}%)`
                        : ""}
                      :
                    </span>
                    <span className={styles["info-value"]}>
                      {formatWholeAmount(orderDetails.pricing.adminFee)}{" "}
                      IQD
                    </span>
                  </div>
                )}
                {/* Loyalty points the patient redeemed — shown as a deduction (green, like
                    the printed invoice) so this breakdown's Total matches the invoice and the
                    amount actually paid. Sourced from the booking's persisted
                    `loyalty_discount`, surfaced on `pricing` by the public endpoint. */}
                {parseFloat(orderDetails.pricing.loyaltyDiscount || 0) > 0 && (
                  <div className={styles["info-row"]}>
                    <span className={styles["info-label"]}>
                      Loyalty Point Discount
                      {orderDetails.pricing.loyaltyPointsUsed
                        ? ` (${orderDetails.pricing.loyaltyPointsUsed} pts)`
                        : ""}
                      :
                    </span>
                    <span
                      className={styles["info-value"]}
                      style={{ color: "#00BC7D" }}
                    >
                      −{" "}
                      {formatWholeAmount(orderDetails.pricing.loyaltyDiscount)}{" "}
                      IQD
                    </span>
                  </div>
                )}
                <div className={styles["info-row"]}>
                  <span className={styles["info-label"]}>
                    <strong>Total:</strong>
                  </span>
                  <span className={styles["info-value"]}>
                    <strong>
                      {formatWholeAmount(
                        orderDetails.pricing.total ||
                          orderDetails.totalAmount ||
                          0,
                      )}{" "}
                      IQD
                    </strong>
                  </span>
                </div>
              </>
            )}
            {orderDetails.scheduledDate && (
              <div className={styles["info-row"]}>
                <span className={styles["info-label"]}>Scheduled Date:</span>
                <span className={styles["info-value"]}>
                  {tzDateNumeric(new Date(orderDetails.scheduledDate))}
                </span>
              </div>
            )}
            {orderDetails.scheduledTime && (
              <div className={styles["info-row"]}>
                <span className={styles["info-label"]}>Scheduled Time:</span>
                <span className={styles["info-value"]}>
                  {orderDetails.scheduledTime}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className={`${styles["detail-card"]} ${styles["card-span-full"]}`}>
          <div className={styles["card-header"]}>
            <FaTruck className={styles["card-icon"]} />
            <h2>Technician Actions</h2>
          </div>
          <div className={styles["card-content"]}>
            {isRejectedOrCancelled ? (
              <div
                style={{
                  padding: "16px 20px",
                  backgroundColor: "#fef2f2",
                  border: "1px solid #ef4444",
                  borderRadius: "8px",
                  color: "#b91c1c",
                  textAlign: "center",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                {isRefundRejection
                  ? "The patient's payment has been refunded by the admin and this booking is now rejected. Technician actions are disabled."
                  : "This booking has been rejected. Technician actions are disabled."}
              </div>
            ) : (
              <>
            {/* Estimated Arrival Time Input (only show if not dispatched yet) */}
            {!workflowStatus.technicianDispatched && (
              <div className={styles["arrival-time-section"]}>
                <div className={styles["arrival-time-row"]}>
                  <label className={styles["arrival-label"]}>
                    Estimated Arrival Time:
                  </label>
                  <div className={styles["arrival-input-container"]}>
                    <input
                      type="number"
                      min="5"
                      max="120"
                      value={estimatedArrivalMinutes}
                      onChange={(e) =>
                        setEstimatedArrivalMinutes(
                          parseInt(e.target.value) || 30,
                        )
                      }
                      className={styles["arrival-time-input"]}
                    />
                    <span className={styles["arrival-time-unit"]}>minutes</span>
                  </div>
                </div>
              </div>
            )}

            <div className={styles["action-buttons"]}>
              {/* Check if this is a HOME_VISIT appointment */}
              {orderDetails.preferredLocation === "HOME_VISIT" ||
              orderDetails.preferredLocation === "Home Visit" ? (
                <>
                  {/* HOME VISIT WORKFLOW */}

                  {/* Step 1: Technician Dispatched */}
                  <button
                    onClick={() => handleWorkflowUpdate("dispatch")}
                    disabled={workflowStatus.technicianDispatched}
                    className={`${styles["action-btn"]} ${workflowStatus.technicianDispatched ? styles["completed"] : styles["pending"]}`}
                  >
                    {workflowStatus.technicianDispatched ? (
                      <FaCheck className={styles["btn-icon"]} />
                    ) : (
                      <FaTruck className={styles["btn-icon"]} />
                    )}
                    {workflowStatus.technicianDispatched
                      ? "Technician Dispatched ✓"
                      : "Mark as Dispatched"}
                  </button>

                  {/* Step 2: Start Sample Collection (renamed from Sample Collected) */}
                  <button
                    onClick={() =>
                      handleWorkflowUpdate("start_sample_collection")
                    }
                    disabled={
                      !workflowStatus.technicianDispatched ||
                      workflowStatus.sampleCollectionStarted
                    }
                    className={`${styles["action-btn"]} ${workflowStatus.sampleCollectionStarted ? styles["completed"] : workflowStatus.technicianDispatched ? styles["active"] : styles["disabled"]}`}
                  >
                    {workflowStatus.sampleCollectionStarted ? (
                      <FaCheck className={styles["btn-icon"]} />
                    ) : (
                      <FaVial className={styles["btn-icon"]} />
                    )}
                    {workflowStatus.sampleCollectionStarted
                      ? "Sample Collection Started ✓"
                      : "Start Sample Collection"}
                  </button>

                  {/* Step 3: Complete Job - Different buttons for COD vs Online */}
                  {orderDetails.paymentMethod === "COD" ||
                  orderDetails.paymentStatus === "PENDING" ? (
                    // COD Payment: Mark Cash Collected and Job Done
                    <button
                      onClick={() => handleWorkflowUpdate("complete_job_cod")}
                      disabled={
                        !workflowStatus.sampleCollectionStarted ||
                        workflowStatus.paymentCollected
                      }
                      className={`${styles["action-btn"]} ${workflowStatus.paymentCollected ? styles["completed"] : workflowStatus.sampleCollectionStarted ? styles["active"] : styles["disabled"]}`}
                    >
                      {workflowStatus.paymentCollected ? (
                        <FaCheck className={styles["btn-icon"]} />
                      ) : (
                        <FaMoneyBillWave className={styles["btn-icon"]} />
                      )}
                      {workflowStatus.paymentCollected
                        ? "Cash Collected & Job Done ✓"
                        : "Mark Cash Collected and Job Done"}
                    </button>
                  ) : (
                    // Online/Wallet Payment: Mark Job Completed
                    <button
                      onClick={() =>
                        handleWorkflowUpdate("complete_job_online")
                      }
                      disabled={
                        !workflowStatus.sampleCollectionStarted ||
                        workflowStatus.sampleCollected
                      }
                      className={`${styles["action-btn"]} ${workflowStatus.sampleCollected ? styles["completed"] : workflowStatus.sampleCollectionStarted ? styles["active"] : styles["disabled"]}`}
                    >
                      {workflowStatus.sampleCollected ? (
                        <FaCheck className={styles["btn-icon"]} />
                      ) : (
                        <FaCheck className={styles["btn-icon"]} />
                      )}
                      {workflowStatus.sampleCollected
                        ? "Job Completed ✓"
                        : "Mark Job Completed"}
                    </button>
                  )}
                </>
              ) : (
                <>
                  {/* ON_SITE WORKFLOW (Original workflow for Visit Center) */}
                  <button
                    onClick={() => handleWorkflowUpdate("dispatch")}
                    disabled={workflowStatus.technicianDispatched}
                    className={`${styles["action-btn"]} ${workflowStatus.technicianDispatched ? styles["completed"] : styles["pending"]}`}
                  >
                    {workflowStatus.technicianDispatched ? (
                      <FaCheck className={styles["btn-icon"]} />
                    ) : (
                      <FaTruck className={styles["btn-icon"]} />
                    )}
                    {workflowStatus.technicianDispatched
                      ? "Technician Dispatched ✓"
                      : "Mark as Dispatched"}
                  </button>

                  <button
                    onClick={() => handleWorkflowUpdate("collect_sample")}
                    disabled={
                      !workflowStatus.technicianDispatched ||
                      workflowStatus.sampleCollected
                    }
                    className={`${styles["action-btn"]} ${workflowStatus.sampleCollected ? styles["completed"] : workflowStatus.technicianDispatched ? styles["active"] : styles["disabled"]}`}
                  >
                    {workflowStatus.sampleCollected ? (
                      <FaCheck className={styles["btn-icon"]} />
                    ) : (
                      <FaVial className={styles["btn-icon"]} />
                    )}
                    {workflowStatus.sampleCollected
                      ? "Sample Collected ✓"
                      : "Mark Sample Collected"}
                  </button>

                  {(orderDetails.paymentMethod === "COD" ||
                    orderDetails.paymentStatus === "PENDING") && (
                    <button
                      onClick={() => handleWorkflowUpdate("collect_payment")}
                      disabled={
                        !workflowStatus.technicianDispatched ||
                        workflowStatus.paymentCollected
                      }
                      className={`${styles["action-btn"]} ${workflowStatus.paymentCollected ? styles["completed"] : workflowStatus.technicianDispatched ? styles["active"] : styles["disabled"]}`}
                    >
                      {workflowStatus.paymentCollected ? (
                        <FaCheck className={styles["btn-icon"]} />
                      ) : (
                        <FaMoneyBillWave className={styles["btn-icon"]} />
                      )}
                      {workflowStatus.paymentCollected
                        ? "Payment Collected ✓"
                        : "Mark Payment Collected"}
                    </button>
                  )}
                </>
              )}
            </div>
              </>
            )}

            {/* Show completion message based on appointment status */}
            {orderDetails.status === "COMPLETED" && (
              <div className={styles["completion-message"]}>
                <FaCheck className={styles["completion-icon"]} />
                <span>Appointment completed successfully!</span>
              </div>
            )}

            {/* Show estimated arrival time if dispatched */}
            {workflowStatus.technicianDispatched &&
              orderDetails.timestamps?.estimatedArrivalTime &&
              orderDetails.status !== "COMPLETED" && (
                <div className={styles["arrival-time-display"]}>
                  <FaTruck className={styles["inline-icon"]} />
                  <span>
                    Estimated Arrival:{" "}
                    {tzCustom(new Date(
                      orderDetails.timestamps.estimatedArrivalTime,
                    ), {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
              )}

            {/* Show current status */}
            <div className={styles["status-info"]}>
              <div className={styles["info-row"]}>
                <span className={styles["info-label"]}>Current Status:</span>
                <span
                  className={`${styles["info-value"]} ${styles["status-badge"]}`}
                >
                  {orderDetails.status || "PENDING"}
                </span>
              </div>
              {orderDetails.paymentStatus && (
                <div className={styles["info-row"]}>
                  <span className={styles["info-label"]}>Payment Status:</span>
                  <span
                    className={`${styles["info-value"]} ${styles["payment-badge"]}`}
                  >
                    {orderDetails.paymentStatus}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={styles["public-footer"]}>
        <p>For any inquiries, please contact our support team.</p>
      </div>
    </div>
  );
};

export default PublicAppointmentDetail;
